import React, { useState } from 'react';
import { useSeason } from '../../context/SeasonContext';
import api from '../../api';
import styles from '../../styles/AdminSeasonOverride.module.css';

const AdminSeasonOverride = () => {
  const {
    currentSeason,
    availableSeasons,
    adminOverride,
    setAdminSeasonOverride,
    clearAdminSeasonOverride,
    triggerSeasonRollover
  } = useSeason();

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [historicalSeasons, setHistoricalSeasons] = useState([]);
  const [showHistoricalData, setShowHistoricalData] = useState(false);

  const handleSeasonChange = async (event) => {
    const selectedSeason = parseInt(event.target.value);
    setLoading(true);
    setMessage('');

    try {
      if (selectedSeason === currentSeason) {
        await clearAdminSeasonOverride();
        setMessage('Admin override cleared');
      } else {
        await setAdminSeasonOverride(selectedSeason);
        setMessage(`Admin override set to ${selectedSeason}`);
      }
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRollover = async () => {
    if (!window.confirm('Are you sure you want to trigger a season rollover? This will:\n\n1. Migrate current season user statistics to historical table\n2. Reset all user statistics to zero for the new season\n3. Switch to the next available season\n\nThis action cannot be undone.')) {
      return;
    }

    setLoading(true);
    setMessage('Starting season rollover process...');

    try {
      const result = await triggerSeasonRollover();
      setMessage(`✅ Season rollover successful!\n\n• Migrated ${currentSeason} user stats to historical table\n• Reset user statistics for new season\n• Switched to season ${result.newSeason}`);
    } catch (error) {
      setMessage(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const [targetSeason, setTargetSeason] = useState(currentSeason);

  const handleMigrationOnly = async () => {
    if (!window.confirm(`Are you sure you want to migrate user statistics for season ${currentSeason} to the historical table? This will preserve the current data but not reset it for a new season.`)) {
      return;
    }

    setLoading(true);
    setMessage('Starting data migration...');

    try {
      await api.migrateUserStats(currentSeason);
      setMessage(`✅ Data migration successful!\n\n• Migrated ${currentSeason} user statistics to historical table\n• Current user statistics remain unchanged`);
      // Refresh historical seasons data
      await loadHistoricalSeasons();
    } catch (error) {
      setMessage(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomSeasonMigration = async () => {
    if (!targetSeason || targetSeason === currentSeason) {
      setMessage('❌ Please select a different target season for migration');
      return;
    }

    if (!window.confirm(`Are you sure you want to migrate the current user statistics to the historical table as season ${targetSeason}?\n\nThis will preserve the current data in the historical table under season ${targetSeason}.`)) {
      return;
    }

    setLoading(true);
    setMessage(`Starting migration to season ${targetSeason}...`);

    try {
      await api.migrateUserStats(targetSeason);
      setMessage(`✅ Data migration successful!\n\n• Migrated current user statistics to historical table as season ${targetSeason}\n• Current user statistics remain unchanged`);
      // Refresh historical seasons data
      await loadHistoricalSeasons();
    } catch (error) {
      setMessage(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadHistoricalSeasons = async () => {
    try {
      const response = await api.getAvailableHistoricalSeasons();
      setHistoricalSeasons(response.data || []);
    } catch (error) {
      console.error('Error loading historical seasons:', error);
    }
  };

  const handleViewHistoricalData = async () => {
    setShowHistoricalData(!showHistoricalData);
    if (!showHistoricalData && historicalSeasons.length === 0) {
      await loadHistoricalSeasons();
    }
  };

  return (
    <div className={styles.adminSeasonOverride}>
      <h3>Season Management</h3>

      <div className={styles.section}>
        <h4>Current Season</h4>
        <p>System is currently using season: <strong>{currentSeason}</strong></p>
      </div>

      <div className={styles.section}>
        <h4>Admin Override</h4>
        <p>Override your session to view the site from a different season perspective:</p>

        <select
          value={adminOverride || currentSeason}
          onChange={handleSeasonChange}
          disabled={loading}
          className={styles.seasonSelect}
        >
          {availableSeasons.map(season => (
            <option key={season} value={season}>
              {season} {season === currentSeason ? '(Current)' : ''}
            </option>
          ))}
        </select>

        {adminOverride && (
          <div className={styles.overrideInfo}>
            <p>⚠️ You are viewing the site in <strong>{adminOverride} Archive</strong> mode</p>
            <p>This only affects your session and will reset on logout</p>
          </div>
        )}
      </div>

      <div className={styles.section}>
        <h4>Data Migration & Season Rollover</h4>
        <p>Manage season transitions and historical data preservation:</p>

        <div className={styles.buttonGroup}>
          <button
            onClick={handleMigrationOnly}
            disabled={loading}
            className={styles.migrationButton}
          >
            {loading ? 'Processing...' : `Migrate ${currentSeason} Data Only`}
          </button>

          <button
            onClick={handleRollover}
            disabled={loading}
            className={styles.rolloverButton}
          >
            {loading ? 'Processing...' : 'Full Season Rollover'}
          </button>
        </div>

        <div className={styles.helpText}>
          <p><strong>Migrate Data Only:</strong> Preserves current season statistics in historical table without changing the current season.</p>
          <p><strong>Full Season Rollover:</strong> Migrates data, resets current statistics, and switches to the next season.</p>
        </div>

        <div className={styles.customMigrationSection}>
          <h5>Custom Season Migration</h5>
          <p>Migrate current user data to a specific historical season (useful for one-off situations):</p>

          <div className={styles.targetSeasonSelector}>
            <label htmlFor="target-season">Target Season:</label>
            <select
              id="target-season"
              value={targetSeason}
              onChange={(e) => setTargetSeason(parseInt(e.target.value))}
              disabled={loading}
              className={styles.seasonSelect}
            >
              {availableSeasons.map(season => (
                <option key={season} value={season}>
                  {season}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={handleCustomSeasonMigration}
            disabled={loading || targetSeason === currentSeason}
            className={styles.customMigrationButton}
          >
            {loading ? 'Processing...' : `Migrate to Season ${targetSeason}`}
          </button>

          <div className={styles.helpText}>
            <p><strong>Custom Migration:</strong> Migrates current user statistics to the historical table under the specified season. Useful when you need to archive data for a season that's already been rolled over.</p>
          </div>
        </div>
      </div>

      <div className={styles.section}>
        <h4>Historical Data</h4>
        <p>View and manage historical season statistics:</p>

        <button
          onClick={handleViewHistoricalData}
          className={styles.historicalButton}
        >
          {showHistoricalData ? 'Hide Historical Data' : 'View Historical Data'}
        </button>

        {showHistoricalData && (
          <div className={styles.historicalData}>
            {historicalSeasons.length > 0 ? (
              <div>
                <h5>Migrated Seasons:</h5>
                <ul>
                  {historicalSeasons.map(season => (
                    <li key={season.season}>
                      <strong>Season {season.season}</strong> - {season.user_count} users migrated on {new Date(season.last_updated).toLocaleDateString()}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p>No historical data has been migrated yet.</p>
            )}
          </div>
        )}
      </div>

      {message && (
        <div className={`${styles.message} ${message.includes('Error') ? styles.error : styles.success}`}>
          {message}
        </div>
      )}
    </div>
  );
};

export default AdminSeasonOverride;
